import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import DataTable from '../components/DataTable';
import WorkshopChat from '../components/WorkshopChat';
import './Customers.css';
import './Cars.css';
import './DetailPages.css';
import './CustomerPortal.css';

function CustomerDetail({ tenantId }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [cars, setCars] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [expandedCars, setExpandedCars] = useState(new Set());
  const [expandedJournals, setExpandedJournals] = useState(new Set());
  const [expandedCustomerJournal, setExpandedCustomerJournal] = useState(false);
  const [expandedInvoices, setExpandedInvoices] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(null);
  const [showCustomerNoteForm, setShowCustomerNoteForm] = useState(false);
  const [noteFormData, setNoteFormData] = useState({ note: '', mileage: '' });
  const [customerNoteFormData, setCustomerNoteFormData] = useState({ note: '' });
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') === 'chat' ? 'chat' : 'overview';
  });

  useEffect(() => {
    if (tenantId && id) {
      loadData();
    }
  }, [tenantId, id]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'chat') {
      setActiveTab('chat');
    } else {
      setActiveTab('overview');
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadCustomer(),
        loadCars(),
        loadRepairs(),
        loadInvoices()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadCustomer = async () => {
    try {
      const data = await api.get(`/customers/${id}`, tenantId);
      setCustomer(data);
    } catch (error) {
      console.error('Error loading customer:', error);
      showNotification('Fehler beim Laden des Kunden', 'error');
      navigate('/customers');
    }
  };

  const loadCars = async () => {
    try {
      const data = await api.get('/cars', tenantId);
      setCars(data.filter(car => car.customerId === id));
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  const loadRepairs = async () => {
    try {
      const data = await api.get('/repairs', tenantId);
      setRepairs(data);
    } catch (error) {
      console.error('Error loading repairs:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await api.get('/invoices', tenantId);
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const getRepairsForCar = (carId) => {
    return repairs
      .filter(repair => repair.carId === carId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const toggleCarHistory = (carId) => {
    const newExpanded = new Set(expandedCars);
    if (newExpanded.has(carId)) {
      newExpanded.delete(carId);
    } else {
      newExpanded.add(carId);
    }
    setExpandedCars(newExpanded);
  };

  const toggleJournal = (carId) => {
    const newExpanded = new Set(expandedJournals);
    if (newExpanded.has(carId)) {
      newExpanded.delete(carId);
    } else {
      newExpanded.add(carId);
    }
    setExpandedJournals(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency === 'CHF' ? 'CHF' : 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleAddNote = async (carId) => {
    if (!noteFormData.note.trim()) return;

    try {
      await api.post(`/cars/${carId}/notes`, {
        note: noteFormData.note,
        mileage: noteFormData.mileage ? parseInt(noteFormData.mileage) : null,
        date: new Date().toISOString()
      }, tenantId);
      await loadCars();
      setShowNoteForm(null);
      setNoteFormData({ note: '', mileage: '' });
    } catch (error) {
      console.error('Error adding note:', error);
      showNotification('Fehler beim Hinzufügen der Notiz', 'error');
    }
  };

  const handleDeleteNote = async (carId, noteId) => {
    if (!window.confirm('Notiz wirklich löschen?')) return;

    try {
      await api.delete(`/cars/${carId}/notes/${noteId}`, tenantId);
      await loadCars();
    } catch (error) {
      console.error('Error deleting note:', error);
      showNotification('Fehler beim Löschen der Notiz', 'error');
    }
  };

  const handleAddCustomerNote = async () => {
    if (!customerNoteFormData.note.trim()) return;

    try {
      await api.post(`/customers/${id}/notes`, {
        note: customerNoteFormData.note,
        date: new Date().toISOString()
      }, tenantId);
      await loadCustomer();
      setShowCustomerNoteForm(false);
      setCustomerNoteFormData({ note: '' });
    } catch (error) {
      console.error('Error adding customer note:', error);
      showNotification('Fehler beim Hinzufügen der Notiz', 'error');
    }
  };

  const handleDeleteCustomerNote = async (noteId) => {
    if (!window.confirm('Notiz wirklich löschen?')) return;

    try {
      await api.delete(`/customers/${id}/notes/${noteId}`, tenantId);
      await loadCustomer();
    } catch (error) {
      console.error('Error deleting customer note:', error);
      showNotification('Fehler beim Löschen der Notiz', 'error');
    }
  };

  const getCarInfo = (car) => {
    return `${car.make || ''} ${car.model || ''} ${car.year || ''}`.trim() || 'Unbekannt';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Ausstehend', symbol: '○' },
      in_progress: { label: 'In Bearbeitung', symbol: '⚙' },
      completed: { label: 'Abgeschlossen', symbol: '✓' }
    };
    const statusInfo = statusMap[status] || { label: status, symbol: '○' };
    return (
      <div className="repair-history-status" title={statusInfo.label}>
        {statusInfo.symbol}
      </div>
    );
  };

  if (loading) {
    return <Spinner />;
  }

  if (!customer) {
    return <div>Kunde nicht gefunden</div>;
  }

  const customerCars = cars.filter(car => car.customerId === id);
  const customerInvoices = invoices.filter(inv => inv.customerId === id).sort((a, b) => new Date(b.date) - new Date(a.date));
  const allCustomerRepairs = repairs
    .filter(repair => customerCars.some(car => car.id === repair.carId))
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const totalInvoiceAmount = customerInvoices.reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
  const totalRepairAmount = allCustomerRepairs.reduce((sum, r) => sum + (r.totalAmount || 0), 0);

  return (
    <div className="customer-detail">
      <div className="detail-header">
        <button type="button" onClick={() => navigate('/customers')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>{customer.firstName} {customer.lastName}</h1>
        <button type="button" onClick={() => navigate(`/customers/${id}/edit`)} className="btn-primary">
          Bearbeiten
        </button>
      </div>

      <div className="customer-portal-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => {
            setActiveTab('overview');
            setSearchParams({});
          }}
        >
          Übersicht
        </button>
        <button
          className={activeTab === 'chat' ? 'active' : ''}
          onClick={() => {
            setActiveTab('chat');
            setSearchParams({ tab: 'chat' });
          }}
        >
          Chat
        </button>
      </div>

      {activeTab === 'chat' ? (
        <div className="portal-section">
          <WorkshopChat customerId={id} tenantId={tenantId} />
        </div>
      ) : (
        <div className="customer-details">
        <div className="detail-section">
          <h2>Kontaktdaten</h2>
          <p><strong>E-Mail:</strong> {customer.email || '-'}</p>
          <p><strong>Telefon:</strong> {customer.phone || '-'}</p>
          {customer.address && (customer.address.street || customer.address.city) && (
            <>
              <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Adresse</h3>
              <p>
                {customer.address.street || ''}<br />
                {customer.address.zip || ''} {customer.address.city || ''}<br />
                {customer.address.country || ''}
              </p>
            </>
          )}
          {tenant?.customerSelfServiceEnabled && (() => {
            const customerInvoice = invoices.find(inv => inv.customerId === id && inv.customerToken);
            const customerPortalUrl = process.env.REACT_APP_CUSTOMER_PORTAL_URL || process.env.CUSTOMER_PORTAL_URL || window.location.origin;
            const fullUrl = customerInvoice?.customerToken 
              ? `${customerPortalUrl}/customer/${encodeURIComponent(customerInvoice.customerToken)}`
              : customerPortalUrl;
            
            return (
              <>
                <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Kundenportal</h3>
                <p>
                  <strong>Portal-URL:</strong><br />
                  {customerInvoice?.customerToken ? (
                    <>
                      <a 
                        href={fullUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#2d3748', textDecoration: 'underline', wordBreak: 'break-all' }}
                      >
                        {fullUrl}
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(fullUrl);
                          showNotification('URL in Zwischenablage kopiert', 'error');
                        }}
                        className="btn-small"
                        style={{ marginLeft: '0.5rem' }}
                      >
                        Kopieren
                      </button>
                    </>
                  ) : (
                    <>
                      <span style={{ color: '#666' }}>
                        {customerPortalUrl}
                      </span>
                      <p style={{ fontSize: '0.875rem', color: '#f57c00', marginTop: '0.5rem' }}>
                        Keine Rechnung mit Portal-Zugang vorhanden. Erstellen Sie eine Rechnung, um einen Zugangscode zu generieren.
                      </p>
                    </>
                  )}
                </p>
                {customerInvoice?.customerToken && (
                  <p style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
                    Kunden können über QR-Codes auf Rechnungen auf ihr Portal zugreifen.
                  </p>
                )}
              </>
            );
          })()}
        </div>

        <div className="detail-section">
          <div 
            className="car-journal-header"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedInvoices(!expandedInvoices);
            }}
            style={{ cursor: 'pointer', marginBottom: expandedInvoices ? '1rem' : '0' }}
          >
            <h2 style={{ margin: 0, display: 'inline' }}>Rechnungen ({customerInvoices.length})</h2>
            <span className="journal-toggle-arrow" style={{ marginLeft: '0.5rem' }}>{expandedInvoices ? '▼' : '▶'}</span>
          </div>
          {expandedInvoices && (
            <>
              {customerInvoices.length === 0 ? (
                <p>Keine Rechnungen vorhanden</p>
              ) : (
                <>
                  <p><strong>Gesamtbetrag:</strong> {formatCurrency(totalInvoiceAmount)}</p>
                  <div style={{ marginTop: '1rem' }}>
                    <DataTable
                      data={customerInvoices}
                      columns={[
                        {
                          key: 'invoiceNumber',
                          header: 'Rechnungsnummer',
                          sortType: 'string',
                          accessor: (row) => row.invoiceNumber || '',
                          render: (row) => (
                            <button 
                              type="button"
                              className="link-button" 
                              onClick={() => navigate(`/invoices/${row.id}`)}
                            >
                              {row.invoiceNumber || ''}
                            </button>
                          )
                        },
                        {
                          key: 'date',
                          header: 'Datum',
                          sortType: 'date',
                          accessor: (row) => row.date || ''
                        },
                        {
                          key: 'status',
                          header: 'Status',
                          sortType: 'string',
                          accessor: (row) => {
                            const statusMap = {
                              'pending': 'Ausstehend',
                              'paid': 'Bezahlt',
                              'overdue': 'Überfällig'
                            };
                            return statusMap[row.status] || row.status || '';
                          }
                        },
                        {
                          key: 'grossAmount',
                          header: 'Betrag',
                          sortType: 'number',
                          accessor: (row) => row.grossAmount || 0,
                          render: (row) => formatCurrency(row.grossAmount || 0)
                        }
                      ]}
                      defaultSort={{ key: 'date', direction: 'desc' }}
                      emptyMessage="Keine Rechnungen vorhanden"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="detail-section">
          <h2>Autos ({customerCars.length})</h2>
          {customerCars.length === 0 ? (
            <p>Keine Autos vorhanden</p>
          ) : (
            <div className="cars-list">
              {customerCars.map(car => {
                const carRepairs = getRepairsForCar(car.id);
                const totalRepairAmount = carRepairs.reduce((sum, r) => sum + (r.totalAmount || 0), 0);
                const isExpanded = expandedCars.has(car.id);
                const isJournalExpanded = expandedJournals.has(car.id);

                return (
                  <div key={car.id} className="car-card">
                    <div className="car-card-header">
                      <div className="car-card-info">
                        <h3>
                          <button 
                            className="link-button" 
                            onClick={() => navigate(`/cars/${car.id}`)}
                            style={{ fontSize: '1em', fontWeight: 'bold' }}
                          >
                            {car.make} {car.model} {car.year && `(${car.year})`}
                          </button>
                        </h3>
                        <div className="car-card-details">
                          <span>Kennzeichen: {car.licensePlate || '-'}</span>
                          <span>Kilometerstand: {car.mileage?.toLocaleString('de-DE') || 0} km</span>
                          {car.reminderDate && (
                            <span style={{ 
                              color: new Date(car.reminderDate) < new Date() ? '#d32f2f' : 
                                     new Date(car.reminderDate) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) ? '#f57c00' : '#666',
                              fontWeight: 'bold'
                            }}>
                              ⏰ Erinnerung: {formatDate(car.reminderDate)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="car-card-actions">
                        <button type="button" onClick={() => navigate(`/cars/${car.id}/edit`)} className="btn-small">
                          Bearbeiten
                        </button>
                      </div>
                    </div>
                    
                    <div className="car-repair-summary">
                      <div className="repair-summary-stats">
                        <span>{carRepairs.length} Reparatur{carRepairs.length !== 1 ? 'en' : ''}</span>
                        {totalRepairAmount > 0 && (
                          <span className="repair-total">Gesamt: {formatCurrency(totalRepairAmount)}</span>
                        )}
                      </div>
                      {carRepairs.length > 0 && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCarHistory(car.id);
                          }}
                          className="btn-small btn-secondary"
                        >
                          {isExpanded ? 'Historie ausblenden' : 'Historie anzeigen'}
                        </button>
                      )}
                    </div>

                    {isExpanded && carRepairs.length > 0 && (
                      <div className="car-repair-history">
                        <h4>Reparaturhistorie</h4>
                        <div className="repair-history-list">
                          {carRepairs.map(repair => (
                            <div 
                              key={repair.id} 
                              className="repair-history-item clickable"
                              onClick={() => navigate(`/repairs/${repair.id}`)}
                            >
                              <div className="repair-history-header">
                                <div className="repair-history-date">
                                  <strong>{repair.repairNumber || '-'}</strong> - {formatDate(repair.date)}
                                  {repair.mileage && (
                                    <span style={{ marginLeft: '0.5rem', fontSize: '0.9em', color: '#666' }}>
                                      ({repair.mileage.toLocaleString('de-DE')} km)
                                    </span>
                                  )}
                                </div>
                                <div className="repair-history-amount">
                                  {formatCurrency(repair.totalAmount || 0)}
                                </div>
                              </div>
                              {repair.description && (
                                <div className="repair-history-description">
                                  {repair.description}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="car-journal-section">
                      <div className="car-journal-header">
                        <h4 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleJournal(car.id);
                          }}
                          style={{ cursor: 'pointer', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <span>Bemerkungen</span>
                          <span className="journal-toggle-arrow">{isJournalExpanded ? '▼' : '▶'}</span>
                        </h4>
                      </div>
                      {isJournalExpanded && (
                        <div className="journal-content">
                          {car.notes && car.notes.length > 0 ? (
                            <div className="journal-list">
                              {car.notes.map((note, idx) => (
                                <div key={idx} className="journal-item">
                                  <div className="journal-item-header">
                                    <span>{formatDate(note.date)}</span>
                                    {note.mileage && <span>{note.mileage.toLocaleString('de-DE')} km</span>}
                                    <button
                                      onClick={() => handleDeleteNote(car.id, note.id)}
                                      className="btn-small btn-danger"
                                    >
                                      Löschen
                                    </button>
                                  </div>
                                  <p>{note.note}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p>Keine Bemerkungen</p>
                          )}
                          {showNoteForm === car.id ? (
                            <div className="note-form">
                              <textarea
                                value={noteFormData.note}
                                onChange={(e) => setNoteFormData({ ...noteFormData, note: e.target.value })}
                                placeholder="Bemerkung eingeben..."
                                rows={3}
                              />
                              <input
                                type="text"
                                value={noteFormData.mileage}
                                onChange={(e) => setNoteFormData({ ...noteFormData, mileage: e.target.value })}
                                placeholder="Kilometerstand (optional)"
                              />
                              <div>
                                <button type="button" onClick={() => handleAddNote(car.id)} className="btn-small">Hinzufügen</button>
                                <button type="button" onClick={() => setShowNoteForm(null)} className="btn-small btn-secondary">Abbrechen</button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowNoteForm(car.id)}
                              className="btn-small"
                            >
                              Notiz hinzufügen
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="detail-section">
          <h2>Bemerkungen</h2>
          <div 
            className="car-journal-header"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedCustomerJournal(!expandedCustomerJournal);
            }}
            style={{ cursor: 'pointer', marginBottom: '1rem' }}
          >
            <span>Bemerkungen ({customer.notes?.length || 0})</span>
            <span className="journal-toggle-arrow">{expandedCustomerJournal ? '▼' : '▶'}</span>
          </div>
          {expandedCustomerJournal && (
            <div className="journal-content">
              {customer.notes && customer.notes.length > 0 ? (
                <div className="journal-list">
                  {customer.notes.map((note, idx) => (
                    <div key={idx} className="journal-item">
                      <div className="journal-item-header">
                        <span>{formatDate(note.date)}</span>
                        <button
                          onClick={() => handleDeleteCustomerNote(note.id)}
                          className="btn-small btn-danger"
                        >
                          Löschen
                        </button>
                      </div>
                      <p>{note.note}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p>Keine Bemerkungen</p>
              )}
              {showCustomerNoteForm ? (
                <div className="note-form">
                  <textarea
                    value={customerNoteFormData.note}
                    onChange={(e) => setCustomerNoteFormData({ note: e.target.value })}
                    placeholder="Bemerkung eingeben..."
                    rows={3}
                  />
                  <div>
                    <button type="button" onClick={handleAddCustomerNote} className="btn-small">Hinzufügen</button>
                    <button type="button" onClick={() => setShowCustomerNoteForm(false)} className="btn-small btn-secondary">Abbrechen</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCustomerNoteForm(true)}
                  className="btn-small"
                >
                  Notiz hinzufügen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

export default CustomerDetail;

